import { useCallback, useEffect, useRef, useState } from "react";
import Cookies from "js-cookie";

type StorageType = "localStorage" | "sessionStorage" | "cookie" | "memory";

interface Options {
  storage?: StorageType;
  cookieDays?: number;
  debug?: boolean;
}

const memoryStore = new Map<string, string>();

function safeJsonParse<T>(raw: string | null | undefined): T | undefined {
  if (raw == null) return undefined;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return undefined;
  }
}

function readRaw(key: string, storage: StorageType): string | null | undefined {
  if (storage === "memory") return memoryStore.get(key);
  if (typeof window === "undefined") return undefined;

  if (storage === "localStorage") return window.localStorage.getItem(key);
  if (storage === "sessionStorage") return window.sessionStorage.getItem(key);
  return Cookies.get(key); // cookie
}

function writeRaw(
  key: string,
  storage: StorageType,
  value: string,
  cookieDays: number
) {
  if (storage === "memory") {
    memoryStore.set(key, value);
    return;
  }

  if (typeof window === "undefined") return;

  if (storage === "localStorage") window.localStorage.setItem(key, value);
  else if (storage === "sessionStorage") window.sessionStorage.setItem(key, value);
  else {
    Cookies.set(key, value, {
      expires: cookieDays,
      sameSite: "lax",
      path: "/",
    });
  }
}

export function usePersistentState<T>(
  key: string,
  initialValue: T,
  options: Options = {}
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const { storage = "localStorage", cookieDays = 365, debug = false } = options;

  const [state, _setState] = useState<T>(() => {
    const raw = readRaw(key, storage);
    const parsed = safeJsonParse<T>(raw);
    return parsed ?? initialValue;
  });

  const keyRef = useRef(key);
  const storageRef = useRef(storage);
  const cookieDaysRef = useRef(cookieDays);

  useEffect(() => {
    keyRef.current = key;
    storageRef.current = storage;
    cookieDaysRef.current = cookieDays;
  }, [key, storage, cookieDays]);

  // Write initial value if nothing existed
  useEffect(() => {
    try {
      const raw = readRaw(key, storage);
      if (raw == null) {
        writeRaw(key, storage, JSON.stringify(initialValue), cookieDays);
      }
    } catch (e) {
      if (debug) console.error("[usePersistentState] init write failed:", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, storage]);

  // Backup write on state changes (in case something bypasses setter)
  useEffect(() => {
    try {
      writeRaw(key, storage, JSON.stringify(state), cookieDays);
    } catch (e) {
      if (debug) console.error("[usePersistentState] effect write failed:", e);
    }
  }, [key, storage, cookieDays, state, debug]);

  const debugRef = useRef(debug);
  debugRef.current = debug;

  // The important part: persist inside setter (synchronous + reliable).
  //
  // Stabilny na stale: klucz, storage i cookieDays czytamy z refow, wiec setter nie
  // musi powstawac na nowo przy kazdym renderze. Gdy powstawal, kazdy efekt trzymajacy
  // go w zaleznosciach uruchamial sie w kolko.
  const setState: React.Dispatch<React.SetStateAction<T>> = useCallback((updater) => {
    _setState((prev) => {
      const next =
        typeof updater === "function"
          ? (updater as (p: T) => T)(prev)
          : updater;

      try {
        const k = keyRef.current;
        const s = storageRef.current;
        const d = cookieDaysRef.current;
        writeRaw(k, s, JSON.stringify(next), d);
      } catch (e) {
        if (debugRef.current)
          console.error("[usePersistentState] setter write failed:", e);
      }

      return next;
    });
  }, []);

  return [state, setState];
}

export const KEYS = {
    SELECTED_COMPANY_ID: 'selected-company-id'
}
